import React, { Component } from "react";
import {
  alert,
  GET,
  POST,
  PUT,
  loading,
  required,
  Pagination,
  Navbar,
  Modal,
  copyText,
} from "../../components/CustomComponent.js";

// ---------------------------------------------------------------------------
// Permission matrix is derived from `global.menu` so the ordering (and the
// labels / icons) always match the sidebar. Keys are the API permission
// prefixes and are unchanged.
// ---------------------------------------------------------------------------
const ACTIONS = ["read", "create", "update", "delete"];
const ACTION_META = {
  read: { label: "Read", icon: "" },
  create: { label: "Create", icon: "" },
  update: { label: "Update", icon: "" },
  delete: { label: "Delete", icon: "" },
};
// Granted automatically by the Navbar, never stored on a role.
const HIDDEN_KEYS = ["dashboard", "developer-mode"];
// Permissions that exist in the API but are not shown in the sidebar.
const EXTRA_ITEMS = [
  { key: "reconciles", label: "Reconcile", icon: "" },
  { key: "certificates", label: "Certificate", icon: "" },
  { key: "wallets", label: "Wallet", icon: "" },
];
const GROUP_ICONS = {
  Main: "",
  Payment: "",
  Management: "",
  Reports: "",
  Commission: "",
  Monitoring: "",
  Configuration: "",
  "Access & System": "",
  Other: "",
};

const buildPermissionGroups = () => {
  const groups = [];
  const seen = new Set();
  (global.menu || []).forEach((item) => {
    if (item.title) {
      groups.push({ title: item.title, items: [] });
      return;
    }
    const key = (item.permission || "").split(".")[0];
    if (!key || HIDDEN_KEYS.includes(key) || seen.has(key)) return;
    if (!groups.length) groups.push({ title: "Main", items: [] });
    seen.add(key);
    groups[groups.length - 1].items.push({
      key,
      label: item.name,
      icon: item.icon,
    });
  });

  const other = EXTRA_ITEMS.filter((it) => !seen.has(it.key));
  other.forEach((it) => seen.add(it.key));
  [...new Set((global.permission || []).map((p) => p.split(".")[0]))]
    .filter((key) => !seen.has(key) && !HIDDEN_KEYS.includes(key))
    .forEach((key) => {
      seen.add(key);
      other.push({
        key,
        label: key.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        icon: "",
      });
    });
  if (other.length) groups.push({ title: "Other", items: other });

  return groups
    .filter((g) => g.items.length)
    .map((g) => ({ ...g, icon: GROUP_ICONS[g.title] || "" }));
};

// `global.js` is imported after the routes in index.js, so the menu is not
// available while this module is evaluated. Build (and cache) lazily instead.
let PERMISSION_META = null;
const getPermissionMeta = () => {
  if (PERMISSION_META) return PERMISSION_META;
  const groups = buildPermissionGroups();
  const keys = groups.flatMap((g) => g.items.map((it) => it.key));
  const all = keys.flatMap((key) => ACTIONS.map((action) => `${key}.${action}`));
  const meta = { groups, keys, all };
  // Only cache once the menu really was available.
  if (global.menu?.length) PERMISSION_META = meta;
  return meta;
};

const toArray = (value) => (Array.isArray(value) ? value : []);

export default class Roles extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modal_create: false,
      modal_update: false,

      filter_search: "",

      data: [],
      loaded: false,
      count: 10,

      id: "",
      uid: "",
      name: "",
      permission: [],
    };
  }

  async componentDidMount() {
    loading(false);
    await this.Get(1);
  }
  Get = async (page) => {
    let data, count;
    loading(true);
    let [result] = await Promise.all([
      GET("v1/admin/roles", { data_search: this.state.filter_search }),
    ]);
    if (result?.status) {
      data = result.data;
      count = 0;
    }
    this.setState({
      data,
      count,
      loaded: true,
    });
    loading(false);
  };
  CreateUpdate = async () => {
    const { uid, name, permission } = this.state;
    if (!name.trim()) {
      alert("", "warning", "Warning", "Please input Name");
      return;
    }
    if (
      await alert(
        "",
        "info",
        "Confirm",
        "Do you want to confirm the submission?",
        "Confirm",
        "Cancel"
      )
    ) {
      loading(true);
      let body = {
        name,
        permission,
      };

      let result = !uid
        ? await POST("v1/admin/roles", body)
        : await PUT("v1/admin/roles/" + uid, body);
      if (result?.status === true) {
        alert("#", "success");
      } else {
        alert(
          "",
          "warning",
          "Warning",
          result?.message || "An error occurred. Please try again."
        );
      }
      loading(false);
    }
  };

  // ---- permission helpers -------------------------------------------------
  has = (perm) => this.state.permission.includes(perm);
  setPermissions = (list, checked) => {
    const { permission } = this.state;
    const updated = checked
      ? [...new Set([...permission, ...list])]
      : permission.filter((p) => !list.includes(p));
    this.setState({ permission: updated });
  };
  countGranted = (permission, keys = getPermissionMeta().keys) =>
    keys.reduce(
      (sum, key) =>
        sum + ACTIONS.filter((a) => permission.includes(`${key}.${a}`)).length,
      0
    );

  // ---- render pieces ------------------------------------------------------
  renderCheck = (checked, onChange, extra = {}) => (
    <label className="pr-check">
      <input type="checkbox" checked={checked} onChange={onChange} {...extra} />
      <span />
    </label>
  );

  renderRoleCard = (item, index) => {
    const { groups: PERMISSION_GROUPS, all: ALL_PERMISSIONS } = getPermissionMeta();
    const permission = toArray(item.permission);
    const granted = this.countGranted(permission);
    const total = ALL_PERMISSIONS.length;
    const pct = total ? Math.round((granted / total) * 100) : 0;
    const initial = (item.name || "?").trim().charAt(0).toUpperCase();
    const coverage = PERMISSION_GROUPS.map((g) => {
      const keys = g.items.map((it) => it.key);
      const modules = keys.filter((key) =>
        ACTIONS.some((a) => permission.includes(`${key}.${a}`))
      ).length;
      return { title: g.title, icon: g.icon, modules, total: keys.length };
    }).filter((g) => g.modules > 0);

    return (
      <div className="col-12 col-md-6 col-xxl-4" key={item.uid || index}>
        <div className="pr-card">
          <div className="pr-card-top">
            <div className="pr-avatar">{initial}</div>
            <div className="flex-grow-1 min-w-0">
              <div className="pr-card-name text-truncate" title={item.name}>
                {item.name || "-"}
              </div>
              <div className="pr-card-uid">
                <span className="text-truncate">{item.uid || "-"}</span>
                <span
                  className="icon pointer ms-2"
                  title="Copy UID"
                  onClick={() => copyText(item.uid || "")}
                >
                  {""}
                </span>
              </div>
            </div>
            {item.is_active !== undefined && (
              <span
                className={`pr-status ${item.is_active ? "on" : "off"}`}
                title={item.is_active ? "Active" : "Inactive"}
              >
                {item.is_active ? "Active" : "Inactive"}
              </span>
            )}
          </div>

          <div className="pr-card-meter">
            <div className="d-flex justify-content-between align-items-end mb-1">
              <span className="pr-meter-label">Permissions</span>
              <span className="pr-meter-value">
                {granted}
                <small> / {total}</small>
              </span>
            </div>
            <div className="pr-bar">
              <span style={{ width: `${pct}%` }} />
            </div>
          </div>

          <div className="pr-chips">
            {coverage.length === 0 && (
              <span className="pr-chip muted">No permission granted</span>
            )}
            {coverage.map((g) => (
              <span
                className={`pr-chip ${g.modules === g.total ? "full" : ""}`}
                key={g.title}
                title={`${g.title}: ${g.modules} of ${g.total} modules`}
              >
                <span className="icon">{g.icon}</span>
                {g.title}
                <b>
                  {g.modules}/{g.total}
                </b>
              </span>
            ))}
          </div>

          <div className="pr-card-actions roles.update">
            <button
              className="pr-edit roles.update"
              onClick={() => {
                this.setState({
                  ...item,
                  permission: toArray(item.permission),
                  modal_update: true,
                });
              }}
            >
              <span className="icon me-2">{""}</span>Edit role
            </button>
          </div>
        </div>
      </div>
    );
  };

  renderMatrix = () => {
    const {
      groups: PERMISSION_GROUPS,
      keys: ALL_KEYS,
      all: ALL_PERMISSIONS,
    } = getPermissionMeta();
    const { permission } = this.state;
    const allChecked = ALL_PERMISSIONS.every((p) => permission.includes(p));
    return (
      <div className="pr-matrix-wrap">
        <table className="pr-matrix">
          <thead>
            <tr>
              <th className="pr-col-module">
                <div className="pr-th-module">
                  <span>Module</span>
                  <small>Ordered like the sidebar</small>
                </div>
              </th>
              <th className="pr-col-all">
                <div className="pr-th">
                  <span>All</span>
                  {this.renderCheck(allChecked, (e) =>
                    this.setPermissions(ALL_PERMISSIONS, e.target.checked)
                  )}
                </div>
              </th>
              {ACTIONS.map((action) => {
                const list = ALL_KEYS.map((key) => `${key}.${action}`);
                return (
                  <th className="pr-col-action" key={action}>
                    <div className="pr-th">
                      <span>
                        <span className="icon me-1">
                          {ACTION_META[action].icon}
                        </span>
                        {ACTION_META[action].label}
                      </span>
                      {this.renderCheck(
                        list.every((p) => permission.includes(p)),
                        (e) => this.setPermissions(list, e.target.checked)
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_GROUPS.map((group) => {
              const groupKeys = group.items.map((it) => it.key);
              const groupList = groupKeys.flatMap((key) =>
                ACTIONS.map((a) => `${key}.${a}`)
              );
              const groupGranted = this.countGranted(permission, groupKeys);
              const groupAll = groupList.every((p) => permission.includes(p));
              return (
                <React.Fragment key={group.title}>
                  <tr className="pr-group">
                    <td className="pr-col-module">
                      <div className="pr-group-title">
                        <span className="icon">{group.icon}</span>
                        {group.title}
                        <em>
                          {groupGranted}/{groupList.length}
                        </em>
                      </div>
                    </td>
                    <td className="pr-col-all">
                      {this.renderCheck(groupAll, (e) =>
                        this.setPermissions(groupList, e.target.checked)
                      )}
                    </td>
                    {ACTIONS.map((action) => {
                      const list = groupKeys.map((key) => `${key}.${action}`);
                      return (
                        <td className="pr-col-action" key={action}>
                          {this.renderCheck(
                            list.every((p) => permission.includes(p)),
                            (e) => this.setPermissions(list, e.target.checked)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {group.items.map((it) => {
                    const rowList = ACTIONS.map((a) => `${it.key}.${a}`);
                    const rowAll = rowList.every((p) => permission.includes(p));
                    const rowAny = rowList.some((p) => permission.includes(p));
                    return (
                      <tr
                        className={`pr-row ${rowAny ? "granted" : ""}`}
                        key={it.key}
                      >
                        <td className="pr-col-module">
                          <div className="pr-module">
                            <span className="pr-module-icon icon">
                              {it.icon || ""}
                            </span>
                            <div>
                              <div className="pr-module-name">{it.label}</div>
                              <div className="pr-module-key">{it.key}</div>
                            </div>
                          </div>
                        </td>
                        <td className="pr-col-all">
                          {this.renderCheck(rowAll, (e) =>
                            this.setPermissions(rowList, e.target.checked)
                          )}
                        </td>
                        {ACTIONS.map((action) => {
                          const perm = `${it.key}.${action}`;
                          return (
                            <td className="pr-col-action" key={action}>
                              {this.renderCheck(this.has(perm), () =>
                                this.setPermissions([perm], !this.has(perm))
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  render() {
    const {
      modal_create,
      modal_update,
      filter_search,
      data,
      loaded,
      count,
      name,
      permission,
    } = this.state;
    const { keys: ALL_KEYS, all: ALL_PERMISSIONS } = getPermissionMeta();
    const roles = toArray(data);
    const selected = this.countGranted(permission);
    const fullAccess = roles.filter(
      (r) => this.countGranted(toArray(r.permission)) === ALL_PERMISSIONS.length
    ).length;
    return (
      <div className="w-100 min-vh-100 premium-report pr-scope">
        <Navbar />
        <div className="body p-3 mt-3">
          {/* HERO */}
          <div className="ar-hero mb-4">
            <div className="ar-hero-head">
              <div>
                <div className="ar-eyebrow mb-1">Access &amp; System</div>
                <div className="ar-title">Role Settings</div>
                <div className="ar-subtitle">
                  Manage user roles and fine-grained permissions for every
                  module in the sidebar.
                </div>
              </div>
              <div className="ar-hero-actions">
                <button
                  className="ar-btn-gold roles.create"
                  onClick={() => {
                    this.setState({
                      modal_create: true,
                      id: "",
                      uid: "",
                      name: "",
                      permission: [],
                    });
                  }}
                >
                  <span className="icon me-2">{""}</span>Create role
                </button>
              </div>
            </div>

            <div className="pr-stats">
              <div className="pr-stat">
                <span className="icon">{""}</span>
                <div>
                  <div className="pr-stat-value">{roles.length}</div>
                  <div className="pr-stat-label">Roles</div>
                </div>
              </div>
              <div className="pr-stat">
                <span className="icon">{""}</span>
                <div>
                  <div className="pr-stat-value">{ALL_KEYS.length}</div>
                  <div className="pr-stat-label">Modules</div>
                </div>
              </div>
              <div className="pr-stat">
                <span className="icon">{""}</span>
                <div>
                  <div className="pr-stat-value">{ALL_PERMISSIONS.length}</div>
                  <div className="pr-stat-label">Permissions</div>
                </div>
              </div>
              <div className="pr-stat">
                <span className="icon">{""}</span>
                <div>
                  <div className="pr-stat-value">{fullAccess}</div>
                  <div className="pr-stat-label">Full access</div>
                </div>
              </div>
            </div>

            <div className="ar-toolbar ar-filter mt-3">
              <div className="ar-filter-head">
                <span className="ar-toolbar-label">
                  <span className="icon me-2">{""}</span>Filter
                </span>
              </div>
              <div className="ar-filter-row">
                <div className="ar-filter-field ar-filter-field-search">
                  <span className="ar-filter-label">Search</span>
              <input
                type="text"
                className="form-control"
                placeholder="Search role name..."
                onChange={(e) => {
                  this.setState({ filter_search: e.target.value });
                }}
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    this.Get(1);
                  }
                }}
                value={filter_search}
              />
                </div>
              <div className="ar-toolbar-actions">
              <button className="ar-btn-gold" onClick={() => this.Get(1)}>
                <span className="icon me-2">{""}</span>Search
              </button>
              <button
                className="ar-btn-ghost"
                onClick={() =>
                  this.setState({ filter_search: "" }, () => this.Get(1))
                }
              >
                Reset
              </button>
              </div>
              </div>
            </div>
          </div>

          {/* ROLE CARDS */}
          <div className="row g-3">
            {roles.map(this.renderRoleCard)}
            {loaded && roles.length === 0 && (
              <div className="col-12">
                <div className="pr-empty">
                  <span className="icon">{""}</span>
                  <div className="fw-bold">No roles found</div>
                  <div className="text-secondary text-14">
                    Try another search or create a new role.
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="w-100 d-flex align-items-center justify-content-end flex-wrap mt-3">
            <Pagination
              className="mb-3"
              count={Math.ceil(count / 10) || 1}
              showFirstButton
              showLastButton
              onChange={(_, page) => this.Get(page)}
            />
          </div>
        </div>

        {/* CREATE UPDATE */}
        <Modal
          show={modal_create || modal_update}
          size="xl"
          dialogClassName="pr-modal pr-scope"
          onHide={() =>
            this.setState({ modal_create: false, modal_update: false })
          }
        >
          <Modal.Header className="pr-modal-header" closeButton>
            <div>
              <div className="pr-modal-eyebrow">
                {modal_create ? "New role" : "Edit role"}
              </div>
              <h5 className="fw-bold mb-0">
                {modal_create ? "Create" : modal_update ? "Update" : ""} Role
              </h5>
            </div>
          </Modal.Header>
          <Modal.Body className="pr-modal-body">
            <div className="row g-3 align-items-end mb-3">
              <div className="col-12 col-lg-6">
                <label className="pr-label">Role name{required}</label>
                <input
                  type="text"
                  className="form-control pr-input"
                  placeholder="e.g. Finance Manager"
                  onChange={(e) => {
                    this.setState({ name: e.target.value });
                  }}
                  value={name}
                />
              </div>
              <div className="col-12 col-lg-6">
                <div className="pr-summary">
                  <div className="flex-grow-1">
                    <div className="pr-summary-value">
                      {selected}
                      <small> / {ALL_PERMISSIONS.length} selected</small>
                    </div>
                    <div className="pr-bar mt-1">
                      <span
                        style={{
                          width: `${
                            ALL_PERMISSIONS.length
                              ? Math.round(
                                  (selected / ALL_PERMISSIONS.length) * 100
                                )
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="pr-quick">
                    <button
                      type="button"
                      onClick={() => this.setPermissions(ALL_PERMISSIONS, true)}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        this.setState({
                          permission: ALL_KEYS.map((key) => `${key}.read`),
                        })
                      }
                    >
                      Read only
                    </button>
                    <button
                      type="button"
                      onClick={() => this.setState({ permission: [] })}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {this.renderMatrix()}
          </Modal.Body>
          <Modal.Footer className="pr-modal-footer">
            <button
              className="btn btn-dark px-5 me-1 rounded-pill"
              onClick={() => {
                this.CreateUpdate();
              }}
            >
              Submit
            </button>
            <button
              className="btn btn-outline-dark px-5 ms-1 rounded-pill"
              onClick={() => {
                this.setState({ modal_create: false, modal_update: false });
              }}
            >
              Cancel
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
